import {
  Flex,
  Box,
  Text,
  HStack,
  Stack,
  IconButton,
  Divider,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  useColorMode,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon, MoonIcon, SunIcon } from "@chakra-ui/icons";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import t from "../translations";

const MotionBox = motion(Box);

const HOME_SECTIONS = [
  { label: t.nav.links.about, id: "about" },
  { label: t.nav.links.education, id: "education" },
  { label: t.nav.links.skills, id: "skills" },
  { label: t.nav.links.experience, id: "experience" },
  { label: t.nav.links.projects, id: "projects" },
  { label: t.nav.links.contact, id: "contact" },
];

const isLearningPath = (p) =>
  p === "/learning" ||
  p.startsWith("/frontEndLearning") ||
  p.startsWith("/backEndLearning") ||
  p.startsWith("/testingLearning") ||
  p.startsWith("/mobileLearning") ||
  p.startsWith("/designLearning");

const PAGE_LINKS = [
  { label: t.nav.links.learning, path: "/learning", isActive: isLearningPath },
  {
    label: t.nav.links.reflections,
    path: "/learnings",
    isActive: (p) => p === "/learnings" || p.startsWith("/learnings/"),
  },
  {
    label: t.nav.links.journey,
    path: "/journey",
    isActive: (p) => p === "/journey",
  },
  {
    label: t.nav.links.resources,
    path: "/resources",
    isActive: (p) => p === "/resources",
  },
];

const DRAWER_LINKS = [
  {
    heading: t.nav.drawer.learning,
    label: "Learning Materials",
    path: "/learning",
    isActive: isLearningPath,
  },
  {
    heading: t.nav.drawer.reflections,
    label: t.nav.drawer.thingsLearned,
    path: "/learnings",
    isActive: (p) => p === "/learnings" || p.startsWith("/learnings/"),
  },
  {
    heading: t.nav.drawer.journey,
    label: t.nav.drawer.myJourney,
    path: "/journey",
    isActive: (p) => p === "/journey",
  },
  {
    heading: t.nav.drawer.resources,
    label: t.nav.drawer.myToolkit,
    path: "/resources",
    isActive: (p) => p === "/resources",
  },
];

export default function Nav({ color }) {
  const [scroll, setScroll] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";

  const navBg = useColorModeValue(
    "rgba(255, 255, 255, 0.9)",
    "rgba(26, 32, 44, 0.9)"
  );
  const drawerBg = useColorModeValue("white", "gray.800");
  const activeBg = useColorModeValue(`${color}.50`, "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.100", "whiteAlpha.100");
  const dividerColor = useColorModeValue("gray.300", "gray.600");

  useEffect(() => {
    const handleScroll = () => setScroll(window.scrollY > 80);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isHome) return;
    const sectionIds = [
      "hero",
      "about",
      "education",
      "skills",
      "experience",
      "projects",
      "contact",
    ];
    const observers = sectionIds.map((id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveSection(id);
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((obs) => obs?.disconnect());
  }, [isHome, location.pathname]);

  const scrollToSection = useCallback(
    (id) => {
      if (isHome) {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 150);
      }
      onClose();
    },
    [isHome, navigate, onClose]
  );

  const goToPage = useCallback(
    (path) => {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
      onClose();
    },
    [navigate, onClose]
  );

  const goHome = () => {
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Flex
        as="header"
        bg={navBg}
        px={{ base: 4, md: 8 }}
        h={16}
        boxShadow={scroll ? "0 4px 20px rgba(128, 90, 213, 0.15)" : "none"}
        zIndex="sticky"
        position="fixed"
        alignItems="center"
        w="100%"
        sx={{
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
        transition="box-shadow 0.3s"
      >
        <Flex flex="1" justify="flex-start" minW={0}>
          <Text
            fontWeight={700}
            fontSize={{ base: "xs", sm: "sm", md: "md" }}
            color={`${color}.400`}
            cursor="pointer"
            onClick={goHome}
            userSelect="none"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            transition="text-shadow 0.3s"
            _hover={{
              textShadow: `0 0 12px var(--chakra-colors-${color}-400)`,
            }}
          >
            {t.nav.logoText}
          </Text>
        </Flex>

        <HStack spacing={6} display={{ base: "none", lg: "flex" }}>
          {HOME_SECTIONS.map(({ label, id }) => {
            const isActive = isHome && activeSection === id;
            return (
              <Box key={id} position="relative" py={1}>
                <Text
                  fontSize="sm"
                  fontWeight={isActive ? 600 : 400}
                  color={isActive ? `${color}.400` : undefined}
                  cursor="pointer"
                  onClick={() => scrollToSection(id)}
                  _hover={{ color: `${color}.400` }}
                  transition="color 0.2s"
                  whiteSpace="nowrap"
                >
                  {label}
                </Text>
                {isActive && (
                  <MotionBox
                    layoutId="nav-underline"
                    position="absolute"
                    bottom={-1}
                    left={0}
                    right={0}
                    h="2px"
                    bg={`${color}.400`}
                    borderRadius="full"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
              </Box>
            );
          })}
          <Box h={4} w="1px" bg={dividerColor} flexShrink={0} />
          {PAGE_LINKS.map(({ label, path, isActive }) => {
            const active = isActive(location.pathname);
            return (
              <Box key={path} position="relative" py={1}>
                <Text
                  fontSize="sm"
                  fontWeight={active ? 600 : 400}
                  color={active ? `${color}.400` : undefined}
                  cursor="pointer"
                  onClick={() => goToPage(path)}
                  _hover={{ color: `${color}.400` }}
                  transition="color 0.2s"
                  whiteSpace="nowrap"
                >
                  {label}
                </Text>
                {active && (
                  <MotionBox
                    layoutId="page-underline"
                    position="absolute"
                    bottom={-1}
                    left={0}
                    right={0}
                    h="2px"
                    bg={`${color}.400`}
                    borderRadius="full"
                    transition={{ type: "spring", stiffness: 350, damping: 30 }}
                  />
                )}
              </Box>
            );
          })}
        </HStack>

        <Flex flex="1" justify="flex-end">
          <HStack spacing={1}>
            <IconButton
              icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              variant="ghost"
              size="sm"
              aria-label={t.nav.ariaLabels.toggleColorMode}
            />
            <IconButton
              icon={<HamburgerIcon />}
              onClick={onOpen}
              variant="ghost"
              size="sm"
              display={{ base: "flex", lg: "none" }}
              aria-label={t.nav.ariaLabels.openMenu}
            />
          </HStack>
        </Flex>
      </Flex>

      <Drawer placement="left" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent bg={drawerBg} maxW="270px">
          <DrawerHeader borderBottomWidth="1px">
            <Flex justify="space-between" align="center">
              <Text fontWeight={700} color={`${color}.400`} fontSize="sm">
                {t.nav.logoText}
              </Text>
              <IconButton
                icon={<CloseIcon w={3} h={3} />}
                onClick={onClose}
                variant="ghost"
                size="sm"
                aria-label={t.nav.ariaLabels.closeMenu}
              />
            </Flex>
          </DrawerHeader>
          <DrawerBody py={4}>
            <Stack spacing={1}>
              <Text
                fontSize="xs"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing="wider"
                color="gray.500"
                px={4}
                pt={2}
                pb={1}
              >
                {t.nav.drawer.home}
              </Text>
              {HOME_SECTIONS.map(({ label, id }) => (
                <Box
                  key={id}
                  py={2}
                  px={4}
                  borderRadius="md"
                  cursor="pointer"
                  fontSize="sm"
                  fontWeight={isHome && activeSection === id ? 600 : 400}
                  color={
                    isHome && activeSection === id
                      ? `${color}.400`
                      : undefined
                  }
                  bg={
                    isHome && activeSection === id ? activeBg : "transparent"
                  }
                  _hover={{ bg: hoverBg }}
                  onClick={() => scrollToSection(id)}
                  transition="all 0.15s"
                >
                  {label}
                </Box>
              ))}

              {DRAWER_LINKS.map((link) => {
                const active = link.isActive(location.pathname);
                return (
                  <Box key={link.path}>
                    <Divider my={3} />
                    <Text
                      fontSize="xs"
                      fontWeight={700}
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="gray.500"
                      px={4}
                      pt={2}
                      pb={1}
                    >
                      {link.heading}
                    </Text>
                    <Box
                      py={2}
                      px={4}
                      borderRadius="md"
                      cursor="pointer"
                      fontSize="sm"
                      fontWeight={active ? 600 : 400}
                      color={active ? `${color}.400` : undefined}
                      bg={active ? activeBg : "transparent"}
                      _hover={{ bg: hoverBg }}
                      onClick={() => goToPage(link.path)}
                      transition="all 0.15s"
                    >
                      {link.label}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
